import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Home, 
  Briefcase, 
  FileText, 
  MessageSquare, 
  User, 
  Settings,
  LogOut,
  Building2,
  Users,
  PlusCircle,
  ClipboardList,
  LifeBuoy,
  Award,
  BarChart3,
  ArrowLeftRight,
  Heart,
  CreditCard,
  Menu,
  X,
  ShieldOff,
  Radio
} from 'lucide-react';
import { Button } from './ui/button';

const Sidebar = ({ mobileOpen, onMobileClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    onMobileClose?.();
    await logout();
    navigate('/');
  };

  const applicantLinks = [
    { to: '/dashboard', icon: Home, label: 'Dashboard' },
    { to: '/jobs', icon: Briefcase, label: 'Jobs suchen' },
    { to: '/applications', icon: ClipboardList, label: 'Bewerbungen' },
    { to: '/rescue', icon: LifeBuoy, label: 'Ausbildungs-Rescue' },
    { to: '/anerkennung', icon: Award, label: 'Anerkennung' },
    { to: '/senior', icon: Heart, label: '55+ Modelle' },
    { to: '/documents', icon: FileText, label: 'Dokumente' },
    { to: '/messages', icon: MessageSquare, label: 'Nachrichten' },
    { to: '/blocked', icon: ShieldOff, label: 'Blockierte Stellen' },
    { to: '/telekom-pilot', icon: Radio, label: 'Kompetenz-DNA' },
    { to: '/profile', icon: User, label: 'Profil' },
  ];

  const employerLinks = [
    { to: '/dashboard', icon: Home, label: 'Dashboard' },
    { to: '/employer/jobs', icon: Briefcase, label: 'Meine Jobs' },
    { to: '/employer/jobs/new', icon: PlusCircle, label: 'Job erstellen' },
    { to: '/employer/applications', icon: Users, label: 'Bewerber' },
    { to: '/employer/heatmap', icon: BarChart3, label: 'Skill-Heatmap' },
    { to: '/employer/redeployment', icon: ArrowLeftRight, label: 'Redeployment' },
    { to: '/messages', icon: MessageSquare, label: 'Nachrichten' },
    { to: '/employer/profile', icon: Building2, label: 'Unternehmen' },
  ];

  const links = user?.user_type === 'employer' ? employerLinks : applicantLinks;

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="p-6 border-b border-border flex items-center justify-between">
        <h1 className="text-xl font-heading font-bold text-foreground">
          <span className="text-primary">Job</span>Match
        </h1>
        <button
          onClick={onMobileClose}
          className="lg:hidden p-1 rounded-lg hover:bg-secondary"
          data-testid="close-sidebar-btn"
        >
          <X className="w-5 h-5 text-muted-foreground" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {links.map((link) => (
          link.to === '/telekom-pilot' ? (
            <NavLink
              key={link.to}
              to={link.to}
              onClick={onMobileClose}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-full text-sm font-semibold transition-all
                ${isActive
                  ? 'bg-[#E20074] text-white shadow-md'
                  : 'bg-[#E20074]/10 text-[#E20074] hover:bg-[#E20074] hover:text-white'
                }`
              }
            >
              <link.icon className="w-5 h-5" strokeWidth={1.5} />
              {link.label}
            </NavLink>
          ) : (
            <NavLink
              key={link.to}
              to={link.to}
              onClick={onMobileClose}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all
                ${isActive 
                  ? 'bg-primary/10 text-primary' 
                  : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                }`
              }
            >
              <link.icon className="w-5 h-5" strokeWidth={1.5} />
              {link.label}
            </NavLink>
          )
        ))}
      </nav>

      {/* User Info & Logout */}
      <div className="p-4 border-t border-border">
        <div className="flex items-center gap-3 px-4 py-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-full h-full rounded-full object-cover" />
            ) : (
              <User className="w-5 h-5 text-primary" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>
        
        <NavLink
          to="/settings"
          onClick={onMobileClose}
          data-testid="nav-settings"
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all mb-1
            ${isActive 
              ? 'bg-primary/10 text-primary' 
              : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
            }`
          }
        >
          <Settings className="w-5 h-5" strokeWidth={1.5} />
          Einstellungen
        </NavLink>

        <Button
          variant="ghost"
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full justify-start gap-3 px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/10"
        >
          <LogOut className="w-5 h-5" strokeWidth={1.5} />
          Abmelden
        </Button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 h-screen w-64 bg-muted/50 border-r border-border flex-col z-40" data-testid="sidebar">
        {sidebarContent}
      </aside>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={onMobileClose} />
          <aside className="absolute left-0 top-0 h-full w-72 bg-background border-r border-border flex flex-col shadow-xl animate-in slide-in-from-left duration-200" data-testid="sidebar-mobile">
            {sidebarContent}
          </aside>
        </div>
      )}
    </>
  );
};

export default Sidebar;
